import {Component, ViewEncapsulation} from '@angular/core';
import {Shared} from "../../shared/shared";
import {AiConfigService} from "../../../service/AiConfigService";
import {CourseService} from "../../../service/CourseService";
import {CourseEntity} from "../../../model/CourseEntity";
import {
  AiConfigDto,
  AiConfigSuggestionDto,
  AiPromptPreviewDto,
  AiSettingDefDto
} from "../../../dto/AiConfigDto";

/**
 * Configuración de la IA por tipo de examen.
 *
 * De dónde viene esta pantalla: había un prompt clonado por curso, y con doscientos cursos eso son
 * dos mil filas y corregir una errata del prompt base son dos mil UPDATE. Ahora un curso no
 * describe cómo son sus preguntas, dice a qué TIPO DE EXAMEN se parece, y el tipo de examen lo
 * describe una vez.
 *
 * Tres pestañas, y la tercera es la importante:
 *  - Tipos de examen: el JSON de cada ámbito. Sólo lo que ese ámbito CAMBIA, no la configuración
 *    entera: lo demás lo hereda de quien tenga puesto arriba.
 *  - Variables: el catálogo. Las que lee el código no se pueden crear aquí (haría falta el código
 *    que las lea); las de texto sí, y se usan escribiendo {{clave}} en el prompt el mismo día.
 *  - Preview: qué recibe el modelo de verdad para un curso concreto. Con cuatro escalones de
 *    herencia y el prompt partido en bloques, es el único sitio donde se ve el resultado, y es
 *    donde se caza la errata: una clave mal escrita no falla, simplemente no hace nada.
 */
@Component({
  selector: 'app-ai-config',
  templateUrl: 'aiConfig.html',
  styleUrls: ['aiConfig.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AiConfig {

  segment: 'scopes' | 'catalogue' | 'preview' = 'scopes';

  loading = false;
  saving = false;
  error: string = null;
  mensaje: string = null;

  // -------------------------------------------------------------------------- tipos de examen
  configs: AiConfigDto[] = [];
  profiles: AiConfigDto[] = [];
  /** Copia de trabajo: se edita una copia para que cancelar sea de verdad cancelar. */
  editando: AiConfigDto = null;
  jsonError: string = null;

  scopeTypes = [
    {name: 'PROFILE', value: 'Tipo de examen'},
    {name: 'COURSE', value: 'Curso'},
    {name: 'CORP', value: 'Corporación'},
    {name: 'GLOBAL', value: 'Global'}
  ];

  // ------------------------------------------------------------------------------- variables
  catalogue: AiSettingDefDto[] = [];
  settingTypes: any[] = [];
  nuevaVariable: any = null;

  // --------------------------------------------------------------------------------- preview
  courses: CourseEntity[] = [];
  courseId: number = null;
  promptCodes: any[] = [];
  promptCode = 'QUESTION_GENERATION';
  preview: AiPromptPreviewDto = null;
  previewing = false;
  suggestion: AiConfigSuggestionDto = null;
  suggesting = false;

  constructor(public shared: Shared,
              private aiConfigService: AiConfigService,
              private courseService: CourseService) {
    this.cargar();
  }

  cargar() {
    this.loading = true;
    this.error = null;
    this.aiConfigService.getConfigs().subscribe(x => {
      this.configs = x == null ? [] : x;
      this.profiles = this.configs.filter(c => c.scopeType === 'PROFILE');
      this.loading = false;
    }, () => {
      this.error = 'No se ha podido cargar la configuración.';
      this.loading = false;
    });
    this.aiConfigService.getSettingCatalogue().subscribe(x => this.catalogue = x == null ? [] : x);
    this.aiConfigService.getSettingTypes().subscribe(x => this.settingTypes = x == null ? [] : x);
    this.aiConfigService.getPromptCodes().subscribe(x => this.promptCodes = x == null ? [] : x);
    this.courseService.getCourses(this.shared.CORP_ID).subscribe(x => this.courses = x == null ? [] : x);
  }

  // ------------------------------------------------------------------------- tipos de examen

  nombreDe(config: AiConfigDto): string {
    if (config == null) {
      return '';
    }
    if (config.name != null && config.name !== '') {
      return config.name;
    }
    return config.scopeType === 'COURSE' ? 'Curso ' + config.scopeId : 'Ámbito ' + config.id;
  }

  /** De quién hereda, en texto, para no tener que ir mirando ids a mano. */
  padreDe(config: AiConfigDto): string {
    if (config == null || config.parentId == null) {
      return 'de nadie';
    }
    const padre = this.configs.find(c => c.id === config.parentId);
    return padre == null ? 'de un ámbito borrado (' + config.parentId + ')' : this.nombreDe(padre);
  }

  editar(config: AiConfigDto) {
    this.editando = JSON.parse(JSON.stringify(config));
    this.editando.settings = this.formatear(this.editando.settings);
    this.jsonError = null;
    this.mensaje = null;
  }

  nuevo() {
    this.editando = new AiConfigDto();
    this.editando.corpId = this.shared.CORP_ID;
    this.editando.scopeType = 'PROFILE';
    this.editando.active = true;
    this.editando.settings = '{\n  \n}';
    this.jsonError = null;
    this.mensaje = null;
  }

  cancelar() {
    this.editando = null;
    this.jsonError = null;
  }

  /**
   * Se valida aquí antes de mandarlo. El backend también valida, pero decirlo mientras se escribe
   * evita el viaje de ida y vuelta por una coma de más, que es el error del noventa por ciento de
   * las veces.
   */
  comprobarJson(): boolean {
    this.jsonError = null;
    if (this.editando == null) {
      return false;
    }
    const texto = this.editando.settings == null ? '' : this.editando.settings.trim();
    if (texto === '') {
      this.editando.settings = '{}';
      return true;
    }
    try {
      const parsed = JSON.parse(texto);
      if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        this.jsonError = 'Tiene que ser un objeto: { "clave": "valor" }';
        return false;
      }
      // Una clave que no está en el catálogo no falla: se guarda y no la lee nadie. Por eso hay
      // que avisar aquí, que si no se queda escrita para siempre sin hacer nada.
      const desconocidas = Object.keys(parsed).filter(k => !this.catalogue.some(c => c.key === k));
      if (desconocidas.length > 0) {
        this.jsonError = 'Estas claves no existen en el catálogo y no las va a leer nadie: '
          + desconocidas.join(', ');
      }
      return true;
    } catch (e) {
      this.jsonError = 'El JSON no se puede leer: ' + e.message;
      return false;
    }
  }

  guardar() {
    if (this.editando == null || !this.comprobarJson()) {
      return;
    }
    this.saving = true;
    this.mensaje = null;
    this.aiConfigService.saveConfig(this.editando).subscribe(r => {
      this.saving = false;
      this.mensaje = r == null ? null : r.message;
      if (r != null && r.ok) {
        this.editando = null;
        this.cargar();
      }
    }, () => {
      this.saving = false;
      this.error = 'No se ha podido guardar.';
    });
  }

  borrar(config: AiConfigDto) {
    if (config == null || !confirm('¿Borrar "' + this.nombreDe(config) + '"?')) {
      return;
    }
    this.aiConfigService.deleteConfig(config.id).subscribe(r => {
      this.mensaje = r == null ? null : r.message;
      if (r != null && r.ok) {
        this.editando = null;
        this.cargar();
      }
    });
  }

  /** Mete una clave del catálogo en el JSON que se está editando, con su valor por defecto. */
  anadirClave(def: AiSettingDefDto) {
    if (this.editando == null || def == null) {
      return;
    }
    let objeto: any;
    try {
      objeto = JSON.parse(this.editando.settings == null || this.editando.settings.trim() === ''
        ? '{}' : this.editando.settings);
    } catch (e) {
      this.jsonError = 'Arregla el JSON antes de añadir claves: ' + e.message;
      return;
    }
    if (objeto[def.key] === undefined) {
      objeto[def.key] = def.defaultValue == null ? '' : def.defaultValue;
    }
    this.editando.settings = JSON.stringify(objeto, null, 2);
    this.comprobarJson();
  }

  private formatear(json: string): string {
    if (json == null || json.trim() === '') {
      return '{}';
    }
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch (e) {
      // Si ya estaba roto en base de datos se deja tal cual: reformatearlo escondería el problema.
      return json;
    }
  }

  // ------------------------------------------------------------------------------ variables

  nuevaVariableForm() {
    this.nuevaVariable = {settingKey: '', settingType: 'TEXT', defaultValue: '', description: '', prompts: ''};
    this.mensaje = null;
  }

  guardarVariable() {
    if (this.nuevaVariable == null) {
      return;
    }
    this.saving = true;
    this.aiConfigService.saveSettingDefinition(this.nuevaVariable).subscribe(r => {
      this.saving = false;
      this.mensaje = r == null ? null : r.message;
      if (r != null && r.ok) {
        this.nuevaVariable = null;
        this.aiConfigService.getSettingCatalogue().subscribe(x => this.catalogue = x == null ? [] : x);
      }
    }, () => {
      this.saving = false;
      this.error = 'No se ha podido guardar la variable.';
    });
  }

  borrarVariable(def: AiSettingDefDto) {
    if (def == null || !def.custom || !confirm('¿Borrar la variable ' + def.key + '?')) {
      return;
    }
    this.aiConfigService.deleteSettingDefinition(def.id).subscribe(r => {
      this.mensaje = r == null ? null : r.message;
      this.aiConfigService.getSettingCatalogue().subscribe(x => this.catalogue = x == null ? [] : x);
    });
  }

  get variablesDelCodigo(): AiSettingDefDto[] {
    return this.catalogue.filter(c => !c.custom);
  }

  get variablesPropias(): AiSettingDefDto[] {
    return this.catalogue.filter(c => c.custom);
  }

  // -------------------------------------------------------------------------------- preview

  verPreview() {
    this.previewing = true;
    this.preview = null;
    this.aiConfigService.previewPrompt(this.courseId, this.promptCode).subscribe(x => {
      this.preview = x;
      this.previewing = false;
    }, () => {
      this.previewing = false;
      this.error = 'No se ha podido montar el preview.';
    });
  }

  sugerir() {
    if (this.courseId == null) {
      this.mensaje = 'Elige un curso: la sugerencia sale de sus propias preguntas.';
      return;
    }
    this.suggesting = true;
    this.suggestion = null;
    this.aiConfigService.suggestConfig(this.courseId).subscribe(x => {
      this.suggestion = x;
      this.suggesting = false;
    }, () => {
      this.suggesting = false;
      this.error = 'No se ha podido deducir la configuración.';
    });
  }

  /** Las claves de la sugerencia, para pintarlas con su porqué al lado. */
  clavesSugeridas(): string[] {
    return this.suggestion == null || this.suggestion.settings == null
      ? [] : Object.keys(this.suggestion.settings);
  }

  /** Copia la sugerencia al portapapeles, para pegarla en el JSON del ámbito que toque. */
  copiarSugerencia() {
    if (this.suggestion == null) {
      return;
    }
    const texto = JSON.stringify(this.suggestion.settings, null, 2);
    if (navigator != null && (navigator as any).clipboard != null) {
      (navigator as any).clipboard.writeText(texto);
      this.mensaje = 'Sugerencia copiada. Pégala en el JSON del tipo de examen o del curso.';
    }
  }
}
